import React, { useEffect, useState } from 'react';
import { useHeader } from '../../context/HeaderContext';
import { createCampaignModificationRequest, getAllCampaigns } from '../../api/campaign';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { MdCloudUpload, MdClose } from 'react-icons/md';

const CreateModificationRequest = () => {
  const { setHeaderTitle } = useHeader();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [campaignsLoading, setCampaignsLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [formData, setFormData] = useState({
    campaign: '',
    message: '',
  });
  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [errors, setErrors] = useState({});

  const MAX_IMAGES = 5;

  useEffect(() => {
    setHeaderTitle('Modify Campaign');
    fetchCampaigns();
  }, [setHeaderTitle]);

  const fetchCampaigns = async () => {
    try {
      setCampaignsLoading(true);
      const data = await getAllCampaigns();
      setCampaigns(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      toast.error('Failed to load campaigns');
    } finally {
      setCampaignsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);

    if (images.length + files.length > MAX_IMAGES) {
      toast.error(`You can only upload up to ${MAX_IMAGES} images`);
      return;
    }

    files.forEach(file => {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Each image should be less than 5MB');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setImages(prev => [...prev, reader.result]);
        setImagePreviews(prev => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.campaign) {
      newErrors.campaign = 'Campaign selection is required';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      const requestData = {
        ...formData,
        images: images,
      };
      await createCampaignModificationRequest(requestData);
      toast.success('Modification request created successfully!');
      setTimeout(() => {
        navigate('/campaigns/mod-req');
      }, 1500);
    } catch (error) {
      console.error('Error creating modification request:', error);
      toast.error(error.response?.data?.message || 'Failed to create modification request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      campaign: '',
      message: '',
    });
    setImages([]);
    setImagePreviews([]);
    setErrors({});
  };

  return (
    <div className='w-full'>
      <section className='bg-white rounded-md shadow-sm p-6'>
        <form onSubmit={handleSubmit}>
          {/* Campaign Selection */}
          <div className='mb-4'>
            <label className='block text-sm font-medium text-gray-700 mb-2'>
              <span className='text-red-500'>*</span> Campaign
            </label>
            {campaignsLoading ? (
              <div className='flex items-center gap-2 text-gray-600'>
                <div className='animate-spin rounded-full h-5 w-5 border-b-2 border-primary'></div>
                <span>Loading campaigns...</span>
              </div>
            ) : (
              <select
                name='campaign'
                value={formData.campaign}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border ${
                  errors.campaign ? 'border-red-500' : 'border-gray-300'
                } rounded-md focus:outline-none focus:ring-2 focus:ring-primary`}
                disabled={campaigns.length === 0}
              >
                <option value=''>Select Type of Campaign</option>
                {campaigns.map((campaign) => (
                  <option key={campaign._id || campaign.id} value={campaign._id || campaign.id}>
                    {campaign.title || campaign.campaignTitle}
                  </option>
                ))}
              </select>
            )}
            {errors.campaign && (
              <p className='text-red-500 text-sm mt-1'>{errors.campaign}</p>
            )}
            {!campaignsLoading && campaigns.length === 0 && (
              <p className='text-gray-500 text-sm mt-1'>
                No campaigns available. Please create a campaign first.
              </p>
            )}
          </div>

          {/* Message */}
          <div className='mb-4'>
            <label className='block text-sm font-medium text-gray-700 mb-2'>
              Message
            </label>
            <textarea
              name='message'
              value={formData.message}
              onChange={handleInputChange}
              placeholder='Enter your message'
              rows={5}
              className={`w-full px-3 py-2 border ${
                errors.message ? 'border-red-500' : 'border-gray-300'
              } rounded-md focus:outline-none focus:ring-2 focus:ring-primary resize-none`}
            />
            {errors.message && (
              <p className='text-red-500 text-sm mt-1'>{errors.message}</p>
            )}
          </div>

          {/* Images Upload */}
          <div className='mb-6'>
            <label className='block text-sm font-medium text-gray-700 mb-2'>
              Images
            </label>
            <div className='border-2 border-dashed border-gray-300 rounded-lg p-6'>
              {imagePreviews.length > 0 ? (
                <div className='space-y-4'>
                  <div className='flex flex-wrap gap-4'>
                    {imagePreviews.map((preview, index) => (
                      <div key={index} className='relative w-24 h-24'>
                        <img
                          src={preview}
                          alt={`Preview ${index + 1}`}
                          className='w-full h-full object-cover rounded-lg'
                        />
                        <button
                          type='button'
                          onClick={() => removeImage(index)}
                          className='absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600'
                        >
                          <MdClose size={14} />
                        </button>
                      </div>
                    ))}
                    {images.length < MAX_IMAGES && (
                      <label className='w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-primary'>
                        <MdCloudUpload className='text-2xl text-gray-400' />
                        <span className='text-xs text-gray-400 mt-1'>Add More</span>
                        <input
                          type='file'
                          accept='image/*'
                          onChange={handleImageChange}
                          multiple
                          className='hidden'
                        />
                      </label>
                    )}
                  </div>
                  <p className='text-sm text-gray-500 text-center'>
                    {images.length} of {MAX_IMAGES} images uploaded
                  </p>
                </div>
              ) : (
                <div className='text-center py-4'>
                  <MdCloudUpload className='text-5xl text-gray-400 mx-auto mb-2' />
                  <p className='text-sm text-gray-600 mb-1'>Upload images</p>
                  <p className='text-xs text-gray-400 mb-4'>Max {MAX_IMAGES} images</p>
                  <label className='cursor-pointer'>
                    <span className='px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50'>
                      Choose Files
                    </span>
                    <input
                      type='file'
                      accept='image/*'
                      onChange={handleImageChange}
                      multiple
                      className='hidden'
                    />
                  </label>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className='flex items-center justify-between pt-4 border-t border-gray-200'>
            <button
              type='button'
              onClick={handleReset}
              className='px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors font-medium'
              disabled={loading}
            >
              Reset
            </button>
            <button
              type='submit'
              className='px-6 py-2 bg-primary text-white rounded-md hover:bg-primaryHoverDark transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed'
              disabled={loading || campaignsLoading || campaigns.length === 0}
            >
              {loading ? 'Creating...' : 'Create Request'}
            </button>
          </div>
        </form>
      </section>

      <ToastContainer position="bottom-right" autoClose={2000} />
    </div>
  );
};

export default CreateModificationRequest;
